import type { TOC } from '@ember/component/template-only';
import { PrismLoadingBar, PrismPagination } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, gt } from 'ember-truth-helpers';
import GeneralDatepickersPickerDay from 'lumen/components/general/datepickers/picker-day';
import GeneralDropdownsDropdownExport from 'lumen/components/general/dropdowns/dropdown-export';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import GeneralReportScheduleReportsModals from 'lumen/components/general/report-schedule/reports-modals';
import MarketRatesOtaSelectionsMarketRatesSelections from 'lumen/components/market/rates/ota-selections/market-rates-selections';
import MarketRatesTablesTableMarketRates from 'lumen/components/market/rates/tables/table-market-rates';
import type MarketMarketratesController from 'lumen/controllers/market/marketrates';
import can from 'lumen/helpers/can';
import hasFeature from 'lumen/helpers/has-feature';

interface MarketMarketratesSignature {
  Args: {
    controller: MarketMarketratesController;
    model: unknown;
  };
}

<template>
  {{! @glint-nocheck: Need to convert controller to *.ts }}

  <GeneralPageHeader>
    <div class="content-wrapper">
      <div class="flex flex-row items-center justify-between">
        <GeneralDatepickersPickerDay
          @compareType={{@controller.compareType}}
          @compareValue={{@controller.compareValue}}
          @decreasePeriod={{@controller.decreaseOffset}}
          @increasePeriod={{@controller.increaseOffset}}
          @isMonthInPast={{@controller.isPast}}
          @maxDate={{@controller.maxDate}}
          @minDate={{@controller.minDate}}
          @nextOffsetDisabled={{@controller.nextOffsetDisabled}}
          @prevOffsetDisabled={{@controller.prevOffsetDisabled}}
          @selectedDay={{@controller.dayMoment}}
          @selectionsChanged={{@controller.selectionsChanged}}
        />
        <GeneralDropdownsDropdownExport
          @export={{@controller.handleExport}}
          @exportChoices={{@controller.exportChoices}}
          @isScheduleDisabled={{@controller.isScheduleDisabled}}
        />
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageHeader @isSmall={{true}}>
    <div class="content-wrapper flex items-center">
      <MarketRatesOtaSelectionsMarketRatesSelections
        @activeView={{@controller.view}}
        @brands={{@controller.brands}}
        @compareType={{@controller.compareType}}
        @compareValue={{@controller.compareValue}}
        @isMonthInPast={{@controller.isPast}}
        @isTableViewActive={{true}}
        @managementTypes={{@controller.managementTypes}}
        @monthMoment={{@controller.monthMoment}}
        @rateParam={{@controller.rateParam}}
        @selected={{@controller.selectedBrands}}
        @selectedDayComponent={{@controller.dayComponent}}
        @selectedLos={{@controller.los}}
        @selectedManagementTypes={{@controller.selectedManagementTypes}}
        @selectedMaxPersons={{@controller.maxPersons}}
        @selectedMealPlan={{@controller.mealPlan}}
        @selectedMembershipType={{@controller.membershipType}}
        @selectedOTAId={{@controller.otaId}}
        @selectedRoomType={{@controller.roomType}}
        @selectionsChanged={{@controller.selectionsChanged}}
        @type="rates"
      />
      {{#if @controller.model.loading}}
        <div class="update-timestamp">
          <div class="update-timestamp-loading-placeholder loading-pulse"></div>
        </div>
      {{else if
        (and @controller.hasUpdateTimestamp @controller.showUpdateTime)
      }}
        <div class="update-timestamp text-200--regular text-nowrap">{{t
            "general.updated"
          }}
          {{@controller.updateTimestamp}}</div>
      {{/if}}
    </div>
  </GeneralPageHeader>

  <GeneralPageMain>
    <div
      class="content-wrapper main-content--new-layout market-rates flex h-full flex-col"
    >
      {{#if @controller.model.loading}}
        <PrismLoadingBar
          @isFixed={{true}}
          @positionTop={{@controller.mainLoadingBarPositionTop}}
        />
      {{/if}}

      <MarketRatesTablesTableMarketRates
        @changeRowId={{@controller.changeRowId}}
        @expandedRowIds={{@controller.expandedRowIds}}
        @message={{@controller.noDataForSelection}}
        @model={{@controller.model}}
        @params={{@controller.params}}
        @startDate={{@controller.startDate}}
        class="overflow-hidden"
      />

      {{#unless @controller.model.loading}}
        <div class="pagination-row-container">
          <PrismPagination
            @lastPage={{@controller.totalPages}}
            @page={{@controller.page}}
            @setPage={{@controller.setPage}}
          />
        </div>
      {{/unless}}
    </div>
  </GeneralPageMain>

  <GeneralReportScheduleReportsModals
    @brands={{@controller.brands}}
    @canCreateNewUser={{false}}
    @canCreateOtherReports={{can "market.reports.create.other.existing"}}
    @compareType={{@controller.compareType}}
    @compareValue={{@controller.compareValue}}
    @compsetAvailable={{and
      (hasFeature "compsets.secondary_compset_supported")
      (gt @controller.hotelAndMarketService.availableCompsets.length 1)
    }}
    @isShowingScheduleModal={{@controller.scheduleReport}}
    @los={{@controller.los}}
    @managementTypes={{@controller.managementTypes}}
    @maxPersons={{@controller.maxPersons}}
    @mealPlan={{@controller.mealPlan}}
    @membershipType={{@controller.membershipType}}
    @onCloseScheduleModal={{@controller.closeScheduleModal}}
    @otaId={{@controller.otaId}}
    @rateParam={{@controller.rateParam}}
    @roomType={{@controller.roomType}}
    @selectedOTA={{@controller.selectedOTA}}
    @type="marketrates"
  />
  {{outlet}}
</template> satisfies TOC<MarketMarketratesSignature>;
